import { useCallback, useMemo } from "react";
import { MaterialReactTable, createMRTColumnHelper, useMaterialReactTable } from "material-react-table";
import { Box, Typography, Button, Tooltip, IconButton, MenuItem } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { Refresh, GppGood, ManageSearch, CheckCircle, Cancel } from "@mui/icons-material";
import dayjs from 'dayjs';
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { notifyError } from "../../../components/common/toast/ToastFunc";

const columnHelper = createMRTColumnHelper();

const columns = [
    columnHelper.accessor("id", {
        header: "Transaction ID",
        size: 100,
    }),
    columnHelper.accessor("from_acc", {
        header: "From Acc",
        size: 100,
    }),
    columnHelper.accessor("to_acc", {
        header: "To Acc",
        size: 100,
    }),
    columnHelper.accessor("status", {
        header: "Status",
        size: 50,
    }),
    columnHelper.accessor("amount", {
        header: "Amount",
        cell: (info) => `$ ${info.value}`,
        size: 50,
    }),
    columnHelper.accessor("created_at", {
        header: "Time",
        cell: (info) => dayjs(info.value).format('MM/DD/YYYY HH:mm:ss'),
        size: 50,
    }),
    columnHelper.accessor("type", {
        header: "Type",
        size: 50,
    }),
];

const TransactionTable = (props) => {
    const { data, loading, setReFetch, actionLoading, reviewTransaction, authorizeTransaction, isCustomerTransactionRequest, isMerchantTransactionRequest, updateOTPDialog } = props;

    const theme = useTheme();

    const buttonTheme = useMemo(() => {
        return {
            color: theme.mainColor, fontSize: 13, fontWeight: "bold", borderRadius: 2, borderColor: theme.mainColor,
            '&:hover': {
                backgroundColor: theme.mainColor, color: 'white', fontSize: 13, fontWeight: "bold", borderRadius: 2, borderColor: theme.mainColor
            },
        }
    }, [theme.mainColor]);

    const getReviewStatus = useCallback((status) => {
        switch (status) {
            case "NOT_INITIATED":
                return "Initiate Review";
            case "PENDING":
                return "Review Pending";
            case "COMPLETED":
                return "Review Completed";
            default:
                return "Review";
        }
    }, []);

    const handleExportRows = (rows) => {
        try {

            const doc = new jsPDF();
            const tableData = rows.map((row, index) => [
                index + 1,
                row.original["id"],
                row.original["status"],
                row.original["from_acc"],
                row.original["to_acc"],
                `$${row.original["amount"]}`, // Format amount with dollar sign
                dayjs(row.original["created_at"]).format('MM/DD/YYYY HH:mm:ss').toString(), // Format date
                row.original["type"],
            ]);

            const tableHeaders = ["No.", "Transaction ID", "Status", "From Account", "To Account", "Amount", "Date & Time", "Type"];

            autoTable(doc, {
                head: [tableHeaders],
                body: tableData,
            });

            doc.save("transaction_history_list.pdf");
        } catch (error) {
            notifyError("Something went wrong");
        }
    };


    const table = useMaterialReactTable({
        columns,
        data,
        state: { showLoadingOverlay: actionLoading || loading },
        initialState: { columnVisibility: { id: false, type: !isCustomerTransactionRequest } },
        muiCircularProgressProps: {
            color: "primary",
            thickness: 5,
            size: 55,
        },
        muiSkeletonProps: {
            animation: "pulse",
            height: 28,
        },
        enableFullScreenToggle: false,
        enableRowSelection: true,
        enableRowActions: !isMerchantTransactionRequest,
        paginationDisplayMode: "pages",
        muiPaginationProps: {
            rowsPerPageOptions: [5, 10, 15, 20, 30],
        },
        positionToolbarAlertBanner: "bottom",
        muiTableHeadCellProps: {
            sx: {
                fontSize: "18px",
            },
        },
        muiTableBodyCellProps: ({ row }) => ({
            sx: {
                fontSize: "15px",
                fontWeight: row.getIsSelected() ? "bold" : "normal",
            },
        }),

        renderRowActionMenuItems: ({ row }) => [
            !isMerchantTransactionRequest && !isCustomerTransactionRequest && <Tooltip key="review" title='Review Transaction' placement="left">
                <MenuItem onClick={() => reviewTransaction(row.original.id)} disabled={row.original?.reviewed_status !== "NOT_INITIATED" || loading || actionLoading}>
                    <ManageSearch sx={{ color: theme.mainColor, mr: 1 }} />
                    <Typography sx={{ color: theme.mainColor, fontWeight: "bold" }}>{getReviewStatus(row.original?.reviewed_status)}</Typography>
                </MenuItem>
            </Tooltip>,
            !isMerchantTransactionRequest && !isCustomerTransactionRequest && <Tooltip key="authorize" title='Authorize Transaction' placement="left">
                <MenuItem onClick={() => authorizeTransaction(row.original.id)} disabled={row.original?.is_authorized || loading || actionLoading}>
                    <GppGood sx={{ color: theme.mainColor, mr: 1 }} />
                    <Typography sx={{ color: theme.mainColor, fontWeight: "bold" }}>{row.original?.is_authorized ? "Transaction Authorized" : "Authorize Transaction"}</Typography>
                </MenuItem>
            </Tooltip>,
            !isMerchantTransactionRequest && isCustomerTransactionRequest && <Tooltip key="approve" title='Approve Transaction' placement="left">
                <MenuItem onClick={() => updateOTPDialog(row.original.id, "Approve")} disabled={row.original?.status !== "Review" || loading || actionLoading}>
                    <CheckCircle sx={{ color: theme.mainColor, mr: 1 }} />
                    <Typography sx={{ color: theme.mainColor, fontWeight: "bold" }}>{["Review", "Reject"].includes(row.original?.status) ? "Approve" : "Approved"}</Typography>
                </MenuItem>
            </Tooltip>,
            !isMerchantTransactionRequest && isCustomerTransactionRequest && <Tooltip key="reject" title='Reject Transaction' placement="left">
                <MenuItem onClick={() => updateOTPDialog(row.original.id, "Reject")} disabled={row.original?.status !== "Review" || loading || actionLoading}>
                    <Cancel sx={{ color: theme.mainColor, mr: 1 }} />
                    <Typography sx={{ color: theme.mainColor, fontWeight: "bold" }}>{["Review", "Success"].includes(row.original?.status) ? "Reject" : "Rejected"}</Typography>
                </MenuItem>
            </Tooltip>
        ],

        renderTopToolbarCustomActions: ({ table }) => (
            <Box
                sx={{
                    display: "flex",
                    gap: "5px",
                    padding: "5px",
                    flexWrap: "wrap",
                }}
            >
                <Tooltip arrow title='Refresh Data'>
                    <span>
                        <IconButton
                            disabled={loading || actionLoading}
                            onClick={() => setReFetch((prev) => !prev)}
                            sx={{ color: theme.mainColor }}
                        >
                            <Refresh />
                        </IconButton>
                    </span>
                </Tooltip>
                <Button
                    disabled={
                        table.getPrePaginationRowModel().rows.length === 0 || loading || actionLoading
                    }
                    onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)}
                    startIcon={<FileDownloadIcon />}
                    variant='outlined'
                    sx={{ ...buttonTheme }}
                >
                    Export All
                </Button>
                <Button
                    disabled={
                        (!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()) || loading || actionLoading
                    }
                    onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
                    startIcon={<FileDownloadIcon />}
                    variant='outlined'
                    sx={{ ...buttonTheme }}
                >
                    Export Sel
                </Button>
            </Box >
        ),
    });

    return <MaterialReactTable table={table} />;
};

export default TransactionTable;
